import {
  sendParent, Machine, AnyEventObject, StateMachine,
} from 'xstate';
import { eq } from 'lodash/fp';
import * as Sentry from '@sentry/browser';
import { nanoid } from 'nanoid';

type ErrorEvent = {
  id: string;
  type: 'ERROR';
  error: Error;
  subject?: string;
  data?: any;
};

interface ErrorContext {
  errors: ErrorEvent[];
}

interface ErrorSchema {
  states: {
    idle: {};
  };
}

export const createError = (
  error: Error,
  subject?: string,
  data?: any,
): ErrorEvent => ({
  id: nanoid(),
  type: 'ERROR',
  subject,
  data,
  error,
});

type GenericConsole = Pick<Console, 'error' | 'warn' | 'info' | 'debug'>;
const logger = (client: GenericConsole) => (_, evt: ErrorEvent) => {
  const { error, id, ...rest } = evt;
  client.error(evt.id, error);
  client.info({
    errorId: id,
    ...rest,
  });
};

const logToSentry = (_, evt: ErrorEvent) => {
  Sentry.captureException(evt.error, (scope) => {
    // Both tags are searchable in Sentry: `errorId` matches the id the
    // user sees in the toast, `subject` groups by feature
    // (e.g. subject:terms-conditions).
    scope.setTag('errorId', evt.id);
    if (evt.subject) {
      scope.setTag('subject', evt.subject);
    }
    scope.setContext('Additional data', {
      errorId: evt.id,
      subject: evt.subject,
      data: evt.data,
    });

    return scope;
  });
};

const logToConsole = logger(console);

const createErrorActions = (env: string) => {
  const coreErrorActions = [
    logToSentry,
  ];

  if (['production', 'test'].some(eq(env))) {
    return coreErrorActions;
  }

  return [...coreErrorActions, logToConsole];
};

export const createErrorMachine = (
  id: string,
): StateMachine<unknown, ErrorSchema, ErrorEvent> => Machine<ErrorContext, ErrorSchema, ErrorEvent>({
  id,
  initial: 'idle',
  states: {
    idle: {
      on: {
        ERROR: {
          actions: createErrorActions(process.env.NODE_ENV as string),
        },
      },
    },
  },
});
